/**
 * AGC: BattleTop Component - Reusable battle header with timer and player info
 * AGC: Displays turn timer, player stats (level, attack, defense, energy), and opponent info
 * AGC: Used in HybridPvEBattle, InteractiveP2PBattle, and other battle components
 */

import React from 'react'
import { useBlockchainStats } from '../../hooks/useBlockchainStats'

export interface PlayerStats {
  level?: number
  attack?: number
  defense?: number
  experience?: number
  experienceToNext?: number
}

export interface OpponentInfo {
  label: string // "Bot" or "Opponent"
  level: number
  energy: number
  address?: string // Optional for PvP
}

export interface BattleTopProps {
  // AGC: Player information
  playerAddress: string
  playerStats?: PlayerStats
  playerEnergy: number
  playerBuffs?: number[] // AGC: Buffs selected for this battle
  
  // AGC: Opponent information
  opponentInfo: OpponentInfo
  opponentBuffs?: number[] // AGC: Opponent's buffs
  
  // AGC: Battle state
  currentTurn: number
  turnTimeLeft: number
  turnSubmitted: boolean
  waitingForOpponent: boolean
  battleEnded: boolean
  winner?: string | null
  
  // AGC: Callbacks
  onBackToMenu?: () => void
}

// AGC: Using PNG images instead of emojis
const BUFF_IMAGES: Record<number, string> = { 0: '/shield.png', 1: '/pierce.png', 2: '/haste.png', 3: '/frost.png' }
const BUFF_NAMES: Record<number, string> = { 0: 'SHIELD', 1: 'PIERCE', 2: 'HASTE', 3: 'FROST' }

/**
 * AGC: BattleTop - Clean 3-column battle header
 * AGC: PLAYER 1 - TURN/TIMER - PLAYER 2
 */
export const BattleTop: React.FC<BattleTopProps> = ({
  playerAddress,
  playerStats,
  playerEnergy,
  playerBuffs = [],
  opponentInfo,
  opponentBuffs = [],
  currentTurn,
  turnTimeLeft,
  turnSubmitted,
  waitingForOpponent,
  battleEnded,
  winner,
  onBackToMenu
}) => {
  const isPlayerWinner = winner?.toLowerCase() === playerAddress.toLowerCase()
  
  // AGC: Get player win rate from blockchain stats
  const { stats: blockchainStats } = useBlockchainStats(playerAddress)
  const playerWinRate = blockchainStats?.winRate ?? null

  return (
    <section className="pb-3">
      {/* AGC: Turn & Timer at top - Horizontal layout, no card border */}
      {!battleEnded && !turnSubmitted && (
        <div 
          data-tour="simulation-turn-timer"
          className="flex items-center justify-center gap-2 mb-2 pb-2 border-b border-white/10"
        >
          <span className="text-sm">🔥</span>
          <span className="text-sm font-bold text-white">TURN {currentTurn}</span>
          <span className="text-slate-500">:</span>
          <span
            className={`text-sm font-bold ${
              turnTimeLeft <= 10 ? 'text-red-400 animate-pulse' : 'text-yellow-400'
            }`}
          >
            {turnTimeLeft}s
          </span>
        </div>
      )}

      {waitingForOpponent && !battleEnded && (
        <div className="flex items-center justify-center gap-2 mb-2 pb-2 border-b border-white/10">
          <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          <p className="text-slate-300 text-[10px]">Waiting...</p>
        </div>
      )}

      {battleEnded && (
        <div className="flex flex-col items-center justify-center gap-2 mb-2 pb-2 border-b border-white/10">
          <div className="text-3xl">{isPlayerWinner ? '🎉' : '💀'}</div>
          <h3 className="text-sm font-bold text-white">
            {isPlayerWinner ? 'VICTORY!' : 'DEFEAT'}
          </h3>
          {onBackToMenu && (
            <button
              onClick={onBackToMenu}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-bold py-1.5 px-3 rounded-lg transition-all text-[10px]"
            >
              Back to Menu
            </button>
          )}
        </div>
      )}

      {/* AGC: 2-Column Layout - Player 1 | Player 2 (side by side, full width) */}
      {/* AGC: data-tour wraps both players for step 21 tour cutout */}
      <div data-tour="simulation-player-energy" className="grid grid-cols-2 gap-2 items-start relative">
        
        {/* LEFT: Player 1 */}
        <div className="text-left">
          <h3 className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">PLAYER 1</h3>
          
          {/* Stats */}
          <div className="flex items-center gap-1.5 mb-1.5 text-[9px] text-slate-300 flex-wrap">
            <span>Lvl {playerStats?.level || 1}</span>
            <span className="text-slate-500">•</span>
            <span>⚔️ {playerStats?.attack || 10}</span>
            <span className="text-slate-500">•</span>
            <span>🛡️ {playerStats?.defense || 5}</span>
            <span className="text-slate-500">•</span>
            <span>Win% {playerWinRate !== null ? `${playerWinRate}%` : '—'}</span>
          </div>
          
          {/* Buffs */}
          {playerBuffs.length > 0 ? (
            <div className="flex flex-wrap gap-1 mb-2">
              {playerBuffs.map((buffId, idx) => (
                <span key={idx} className="px-1.5 py-0.5 rounded text-[9px] bg-blue-600/20 border border-blue-400/40 text-blue-300 flex items-center gap-1">
                  <img src={BUFF_IMAGES[buffId]} alt={BUFF_NAMES[buffId]} className="w-3 h-3 object-contain" />
                  {BUFF_NAMES[buffId]}
                </span>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1 mb-2">
              <span className="px-1.5 py-0.5 rounded text-[9px] bg-slate-700/20 border border-slate-500/40 text-slate-400">
                No buffs
              </span>
            </div>
          )}
          
          {/* Energy Bar */}
          <div>
            <div className="flex items-center justify-between text-[9px] text-slate-400 mb-0.5">
              <span>Energy</span>
              <span>{playerEnergy}/100</span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-slate-800 overflow-hidden shadow-inner shadow-black/30">
              <div
                className="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${(playerEnergy / 100) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* RIGHT: Player 2 / Opponent */}
        <div className="text-right">
          <h3 className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{opponentInfo.label.toUpperCase()}</h3>
          
          {/* Stats */}
          <div className="flex items-center justify-end gap-1.5 mb-1.5 text-[9px] text-slate-300 flex-wrap">
            <span>Win% N/A</span>
            <span className="text-slate-500">•</span>
            <span>🛡️ {Math.floor((opponentInfo.level || 1) / 2) + 3}</span>
            <span className="text-slate-500">•</span>
            <span>⚔️ {(opponentInfo.level || 1) + 5}</span>
            <span className="text-slate-500">•</span>
            <span>Lvl {opponentInfo.level}</span>
          </div>
          
          {/* Buffs */}
          {opponentBuffs.length > 0 ? (
            <div className="flex flex-wrap gap-1 justify-end mb-2">
              {opponentBuffs.map((buffId, idx) => (
                <span key={idx} className="px-1.5 py-0.5 rounded text-[9px] bg-red-600/20 border border-red-400/40 text-red-300 flex items-center gap-1">
                  <img src={BUFF_IMAGES[buffId]} alt={BUFF_NAMES[buffId]} className="w-3 h-3 object-contain" />
                  {BUFF_NAMES[buffId]}
                </span>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1 justify-end mb-2">
              <span className="px-1.5 py-0.5 rounded text-[9px] bg-slate-700/20 border border-slate-500/40 text-slate-400">
                No buffs
              </span>
            </div>
          )}
          
          {/* Energy Bar */}
          <div>
            <div className="flex items-center justify-between text-[9px] text-slate-400 mb-0.5">
              <span>{opponentInfo.energy}/100</span>
              <span>Energy</span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-slate-800 overflow-hidden shadow-inner shadow-black/30">
              <div
                className="bg-red-500 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${(opponentInfo.energy / 100) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* AGC: VS text - very small and centered between players */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[10px] font-bold text-white/60 pointer-events-none">
          VS
        </div>
      </div>
    </section>
  )
}

